import React, { useState, useMemo } from 'react';
import { useApi, api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useTheme } from '../context/ThemeContext';
import useMobile from '../hooks/useMobile';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';

const CAT_COLORS = ['#7C6FF7','#F7605C','#34D399','#FBBF24','#60A5FA','#F472B6','#A78BFA','#FB923C','#4ADE80','#F87171'];
const CAT_ICONS  = ['🏠','🚗','🛒','🍽','💡','👗','🎮','🏥','✈️','📚','🐾','💰'];

const PRESET_SUBS = [
  { name:'Netflix',           emoji:'🎬', amount:169 },
  { name:'Viaplay',           emoji:'⚽', amount:199 },
  { name:'Disney+',           emoji:'🏰', amount:109 },
  { name:'Max (HBO)',         emoji:'🎭', amount:109 },
  { name:'Spotify',           emoji:'🎵', amount:119 },
  { name:'Apple Music',       emoji:'🍎', amount:119 },
  { name:'YouTube Premium',   emoji:'▶️',  amount:109 },
  { name:'NRK+',              emoji:'📺', amount:0   },
  { name:'Storytel',          emoji:'📚', amount:129 },
  { name:'Kindle Unlimited',  emoji:'📖', amount:119 },
  { name:'iCloud+',           emoji:'☁️',  amount:15  },
  { name:'Google One',        emoji:'🟡', amount:25  },
  { name:'Strøm',             emoji:'⚡', amount:0   },
  { name:'Internett',         emoji:'🌐', amount:0   },
  { name:'Mobilabonnement',   emoji:'📱', amount:0   },
  { name:'Forsikring',        emoji:'🛡️',  amount:0   },
];

function kr(n) {
  return new Intl.NumberFormat('nb-NO',{style:'currency',currency:'NOK',minimumFractionDigits:0}).format(n||0);
}

function ProgressBar({ spent, budget }) {
  const pct = budget > 0 ? Math.min(100,(spent/budget)*100) : 0;
  const color = spent > budget ? '#F7605C' : pct > 80 ? '#FBBF24' : '#34D399';
  return (
    <div style={{background:'var(--bdr)', borderRadius:20, height:8, overflow:'hidden', flex:1}}>
      <div style={{width:`${pct}%`, height:'100%', background:color, borderRadius:20, transition:'.4s'}} />
    </div>
  );
}

// ─── OVERSIKT-FANE ─────────────────────────────────────────────
function OversiktTab({ catStats, totalBudget, totalSpent, expenses, incomeData, month }) {
  const totalIncome = (incomeData||[]).reduce((s,i) => s+i.amount, 0);
  const balance = totalIncome - totalSpent;

  const pieData = catStats.filter(c=>c.spent>0).map((c,i) => ({
    name: c.name, value: c.spent, color: c.color || CAT_COLORS[i % CAT_COLORS.length],
  }));

  const recentExp = [...expenses].sort((a,b)=>new Date(b.date)-new Date(a.date)).slice(0,5);

  return (
    <div style={{display:'flex', flexDirection:'column', gap:12}}>
      {/* Summering */}
      <div style={b.summaryRow}>
        <div style={b.sumCard}>
          <div style={b.sumLbl}>Inntekter</div>
          <div style={{...b.sumVal, color:'#34D399'}}>{kr(totalIncome)}</div>
        </div>
        <div style={b.sumCard}>
          <div style={b.sumLbl}>Utgifter</div>
          <div style={{...b.sumVal, color:'#F7605C'}}>{kr(totalSpent)}</div>
        </div>
        <div style={b.sumCard}>
          <div style={b.sumLbl}>{balance>=0?'Til overs':'Underskudd'}</div>
          <div style={{...b.sumVal, fontSize:20, color:balance>=0?'#34D399':'#F7605C'}}>{kr(Math.abs(balance))}</div>
        </div>
      </div>

      {/* Diagram */}
      {pieData.length > 0 && (
        <div style={b.card}>
          <div style={b.cardTitle}>Utgifter per kategori</div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(v) => kr(v)} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{display:'flex', flexWrap:'wrap', gap:6, justifyContent:'center', marginTop:4}}>
            {pieData.map((d,i) => (
              <div key={i} style={{display:'flex', alignItems:'center', gap:4, fontSize:11}}>
                <span style={{width:10, height:10, borderRadius:'50%', background:d.color, display:'inline-block'}} />
                <span style={{color:'var(--txm)'}}>{d.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bar-diagram: budsjett vs. faktisk per kategori */}
      {catStats.filter(c => c.budget > 0 || c.spent > 0).length > 0 && (
        <div style={b.card}>
          <div style={b.cardTitle}>Budsjett vs. faktisk</div>
          <ResponsiveContainer width="100%" height={Math.max(160, catStats.filter(c=>c.budget>0||c.spent>0).length * 40)}>
            <BarChart
              layout="vertical"
              data={catStats.filter(c=>c.budget>0||c.spent>0).map(c => ({
                name: (c.icon||'') + ' ' + c.name,
                Budsjett: c.budget || 0,
                Faktisk:  Math.round(c.spent),
                over:     c.spent > c.budget && c.budget > 0,
              }))}
              margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--bdr)" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--txl)' }} tickFormatter={v => `${v} kr`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: 'var(--txm)' }} width={100} />
              <Tooltip
                formatter={(value, name) => [`${new Intl.NumberFormat('nb-NO').format(value)} kr`, name]}
                contentStyle={{ background: 'var(--card)', border: '1px solid var(--bdr)', borderRadius: 10, fontSize: 12 }}
                labelStyle={{ color: 'var(--txt)', fontWeight: 700 }}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Bar dataKey="Budsjett" fill="#7C6FF7" fillOpacity={0.35} radius={[0, 4, 4, 0]} />
              <Bar dataKey="Faktisk" radius={[0, 4, 4, 0]}>
                {catStats.filter(c=>c.budget>0||c.spent>0).map((c, i) => (
                  <Cell key={i} fill={c.spent > c.budget && c.budget > 0 ? '#F7605C' : '#34D399'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div style={{ display:'flex', gap:12, justifyContent:'center', marginTop:6, fontSize:11, color:'var(--txl)' }}>
            <span><span style={{ display:'inline-block', width:10, height:10, borderRadius:2, background:'#7C6FF7', opacity:.6, marginRight:4 }}/>Budsjett</span>
            <span><span style={{ display:'inline-block', width:10, height:10, borderRadius:2, background:'#34D399', marginRight:4 }}/>Under budsjett</span>
            <span><span style={{ display:'inline-block', width:10, height:10, borderRadius:2, background:'#F7605C', marginRight:4 }}/>Over budsjett</span>
          </div>
        </div>
      )}

      {/* Siste transaksjoner */}
      {recentExp.length > 0 && (
        <div style={b.card}>
          <div style={b.cardTitle}>Siste 5 transaksjoner</div>
          {recentExp.map(e => (
            <div key={e.id} style={b.expRow}>
              <div style={{flex:1}}>
                <div style={b.expTitle}>{e.title}</div>
                <div style={b.expMeta}>{new Date(e.date).toLocaleDateString('nb-NO',{day:'numeric',month:'short'})}{e.user_name&&` · ${e.user_name}`}</div>
              </div>
              <div style={{...b.expAmount, color:'#F7605C'}}>{kr(e.amount)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── INNTEKTER-FANE ────────────────────────────────────────────
function InntekterTab({ incomeData, refresh, month, members }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ description:'', amount:'', date: new Date().toISOString().slice(0,10), user_id:'', recurring:false });
  const set = k => e => setForm(f => ({...f,[k]: e.target.type==='checkbox' ? e.target.checked : e.target.value}));

  const save = async e => {
    e.preventDefault();
    await api.post('/api/income', { ...form, amount: Number(form.amount) });
    setForm({ description:'', amount:'', date: new Date().toISOString().slice(0,10), user_id:'', recurring:false });
    setShowForm(false);
    refresh();
  };

  const del = async id => { await api.delete(`/api/income/${id}`); refresh(); };

  const total = (incomeData||[]).reduce((s,i)=>s+i.amount,0);

  return (
    <div style={{display:'flex', flexDirection:'column', gap:10}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8}}>
        <div style={{fontSize:20, fontWeight:800, color:'#34D399'}}>{kr(total)}<span style={{fontSize:12, color:'var(--txl)', fontWeight:400, marginLeft:6}}>total inntekt</span></div>
        <button style={b.addBtn} onClick={()=>setShowForm(v=>!v)}>+ Legg til inntekt</button>
      </div>

      {showForm && (
        <div style={b.card}>
          <form onSubmit={save} style={b.form}>
            <input style={b.inp} placeholder="Beskrivelse (f.eks. Lønn)" value={form.description} onChange={set('description')} required autoFocus />
            <div style={b.twoCol}>
              <div style={b.field}>
                <label style={b.lbl}>Beløp (kr)</label>
                <input style={b.inp} type="number" min="0" value={form.amount} onChange={set('amount')} required />
              </div>
              <div style={b.field}>
                <label style={b.lbl}>Dato</label>
                <input style={b.inp} type="date" value={form.date} onChange={set('date')} required />
              </div>
            </div>
            <div style={b.field}>
              <label style={b.lbl}>Person</label>
              <select style={b.sel} value={form.user_id} onChange={set('user_id')}>
                <option value="">Alle / Familie</option>
                {members.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <label style={{display:'flex', alignItems:'center', gap:8, fontSize:13, color:'var(--txm)', cursor:'pointer'}}>
              <input type="checkbox" checked={form.recurring} onChange={set('recurring')} />
              🔄 Fast inntekt (lønn, trygd, osv.)
            </label>
            <div style={b.btnRow}>
              <button style={b.saveBtn} type="submit">Legg til</button>
              <button style={b.cancelBtn} type="button" onClick={()=>setShowForm(false)}>Avbryt</button>
            </div>
          </form>
        </div>
      )}

      {(incomeData||[]).length === 0 && <div style={b.empty}>Ingen inntekter registrert denne måneden</div>}

      {(incomeData||[]).map(inc => (
        <div key={inc.id} style={b.expRow}>
          <div style={{flex:1}}>
            <div style={b.expTitle}>{inc.recurring ? '🔄 ' : ''}{inc.description}</div>
            <div style={b.expMeta}>{new Date(inc.date).toLocaleDateString('nb-NO',{day:'numeric',month:'short'})}{inc.user_name&&` · ${inc.user_name}`}</div>
          </div>
          <div style={{...b.expAmount, color:'#34D399'}}>{kr(inc.amount)}</div>
          <button style={b.delBtn} onClick={()=>del(inc.id)}>✕</button>
        </div>
      ))}
    </div>
  );
}

// ─── UTGIFTER-FANE ─────────────────────────────────────────────
function UtgifterTab({ catStats, expenses, refresh, categories, members }) {
  const [showForm, setShowForm] = useState(false);
  const [expandedCat, setExpandedCat] = useState(null);
  const [expForm, setExpForm] = useState({ category_id:'', title:'', amount:'', date:new Date().toISOString().slice(0,10), note:'' });
  const [catForm, setCatForm] = useState({ name:'', budget:'', color:'#7C6FF7', icon:'🏠' });
  const [showAddCat, setShowAddCat] = useState(false);
  const setE = k => e => setExpForm(f=>({...f,[k]:e.target.value}));
  const setC = k => e => setCatForm(f=>({...f,[k]:e.target.value}));

  const saveExp = async e => {
    e.preventDefault();
    await api.post('/api/budget/expenses', { ...expForm, amount:Number(expForm.amount) });
    setExpForm(f=>({...f,title:'',amount:'',note:''}));
    setShowForm(false);
    refresh();
  };

  const saveCat = async e => {
    e.preventDefault();
    await api.post('/api/budget/categories', { ...catForm, budget:Number(catForm.budget)||0 });
    setCatForm({ name:'', budget:'', color:'#7C6FF7', icon:'🏠' });
    setShowAddCat(false);
    refresh();
  };

  const delExp = async id => { await api.delete(`/api/budget/expenses/${id}`); refresh(); };
  const delCat = async id => { if(!window.confirm('Slette kategorien?'))return; await api.delete(`/api/budget/categories/${id}`); refresh(); };

  const totalSpent = catStats.reduce((s,c)=>s+c.spent,0);

  return (
    <div style={{display:'flex', flexDirection:'column', gap:10}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8}}>
        <div style={{fontSize:20, fontWeight:800, color:'#F7605C'}}>{kr(totalSpent)}<span style={{fontSize:12, color:'var(--txl)', fontWeight:400, marginLeft:6}}>totalt</span></div>
        <div style={{display:'flex', gap:8}}>
          <button style={{...b.addBtn, fontSize:12}} onClick={()=>setShowAddCat(v=>!v)}>⚙️ Kategorier</button>
          <button style={b.addBtn} onClick={()=>setShowForm(v=>!v)}>+ Ny utgift</button>
        </div>
      </div>

      {showForm && (
        <div style={b.card}>
          <form onSubmit={saveExp} style={b.form}>
            <input style={b.inp} placeholder="Hva kjøpte du?" value={expForm.title} onChange={setE('title')} required autoFocus />
            <div style={b.twoCol}>
              <div style={b.field}><label style={b.lbl}>Beløp (kr)</label><input style={b.inp} type="number" min="0" step="0.01" value={expForm.amount} onChange={setE('amount')} required /></div>
              <div style={b.field}><label style={b.lbl}>Dato</label><input style={b.inp} type="date" value={expForm.date} onChange={setE('date')} required /></div>
            </div>
            <div style={b.field}>
              <label style={b.lbl}>Kategori</label>
              <select style={b.sel} value={expForm.category_id} onChange={setE('category_id')}>
                <option value="">Ingen kategori</option>
                {categories.map(c=><option key={c.id} value={c.id}>{c.icon||'💰'} {c.name}</option>)}
              </select>
            </div>
            <input style={b.inp} placeholder="Notat (valgfritt)" value={expForm.note} onChange={setE('note')} />
            <div style={b.btnRow}><button style={b.saveBtn} type="submit">Legg til</button><button style={b.cancelBtn} type="button" onClick={()=>setShowForm(false)}>Avbryt</button></div>
          </form>
        </div>
      )}

      {showAddCat && (
        <div style={b.card}>
          <div style={b.cardTitle}>Kategorier</div>
          {categories.map(c => (
            <div key={c.id} style={{display:'flex', alignItems:'center', gap:8, padding:'6px 0', borderBottom:'1px solid var(--bdr)'}}>
              <span style={{...b.catIcon, background:c.color+'22', color:c.color, width:32, height:32, fontSize:16}}>{c.icon||'💰'}</span>
              <span style={{flex:1, fontSize:13, fontWeight:600, color:'var(--txt)'}}>{c.name}</span>
              {c.budget>0&&<span style={{fontSize:11,color:'var(--txl)'}}>{kr(c.budget)}/mnd</span>}
              <button style={b.delBtn} onClick={()=>delCat(c.id)}>✕</button>
            </div>
          ))}
          <form onSubmit={saveCat} style={{...b.form, marginTop:8}}>
            <div style={b.twoCol}>
              <input style={b.inp} placeholder="Navn på kategori" value={catForm.name} onChange={setC('name')} required />
              <select style={b.sel} value={catForm.icon} onChange={setC('icon')}>
                {CAT_ICONS.map(i=><option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div style={b.twoCol}>
              <div style={b.field}><label style={b.lbl}>Budsjett/mnd</label><input style={b.inp} type="number" min="0" value={catForm.budget} onChange={setC('budget')} /></div>
              <div style={b.field}>
                <label style={b.lbl}>Farge</label>
                <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                  {CAT_COLORS.map(col=>(
                    <div key={col} onClick={()=>setCatForm(f=>({...f,color:col}))}
                      style={{width:24,height:24,borderRadius:'50%',background:col,cursor:'pointer',border:catForm.color===col?'3px solid var(--txt)':'3px solid transparent'}} />
                  ))}
                </div>
              </div>
            </div>
            <div style={b.btnRow}><button style={b.saveBtn} type="submit">+ Legg til</button><button style={b.cancelBtn} type="button" onClick={()=>setShowAddCat(false)}>Lukk</button></div>
          </form>
        </div>
      )}

      {catStats.length === 0 && <div style={b.empty}>Ingen kategorier — opprett en for å komme i gang</div>}

      {catStats.map((c,i) => (
        <div key={c.id} style={b.catCard}>
          <div style={b.catTop} onClick={()=>setExpandedCat(expandedCat===c.id?null:c.id)}>
            <span style={{...b.catIcon, background:(c.color||CAT_COLORS[i%CAT_COLORS.length])+'22', color:c.color||CAT_COLORS[i%CAT_COLORS.length]}}>{c.icon||'💰'}</span>
            <div style={{flex:1, minWidth:0}}>
              <div style={b.catName}>{c.name}</div>
              {c.budget>0&&<div style={{display:'flex',alignItems:'center',gap:8}}><ProgressBar spent={c.spent} budget={c.budget}/><span style={{fontSize:11,color:'var(--txm)',flexShrink:0}}>{Math.round((c.spent/c.budget)*100)}%</span></div>}
            </div>
            <div style={b.catRight}>
              <div style={{...b.catSpent,color:c.budget>0&&c.spent>c.budget?'#F7605C':'var(--txt)'}}>{kr(c.spent)}</div>
              {c.budget>0&&<div style={b.catBudget}>av {kr(c.budget)}</div>}
              <span style={{fontSize:10,color:'var(--txl)'}}>{expandedCat===c.id?'▲':'▼'}</span>
            </div>
          </div>
          {expandedCat===c.id&&(
            <div style={{borderTop:'1px solid #EAE8F8'}}>
              {c.expenses.length===0&&<div style={{padding:12,color:'var(--txl)',fontSize:13,textAlign:'center'}}>Ingen utgifter i denne kategorien</div>}
              {c.expenses.map(e=>(
                <div key={e.id} style={b.expRow}>
                  <div style={{flex:1}}><div style={b.expTitle}>{e.title}</div><div style={b.expMeta}>{new Date(e.date).toLocaleDateString('nb-NO',{day:'numeric',month:'short'})}{e.user_name&&` · ${e.user_name}`}{e.note&&` · ${e.note}`}</div></div>
                  <div style={b.expAmount}>{kr(e.amount)}</div>
                  <button style={b.delBtn} onClick={()=>delExp(e.id)}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── ABONNEMENTER-FANE ─────────────────────────────────────────
function AbonnementerTab({ subsData, refresh }) {
  const [showCustom, setShowCustom] = useState(false);
  const [customForm, setCustomForm] = useState({ name:'', amount:'', emoji:'💳', billing_cycle:'monthly', next_renewal:'' });
  const setF = k => e => setCustomForm(f=>({...f,[k]:e.target.value}));

  const activeSubs = (subsData||[]).filter(s=>s.active);
  const totalMonthly = activeSubs.reduce((sum,s) => {
    if (s.billing_cycle==='yearly') return sum + s.amount/12;
    return sum + s.amount;
  }, 0);
  const totalYearly = activeSubs.reduce((sum,s) => {
    if (s.billing_cycle==='yearly') return sum + s.amount;
    return sum + s.amount*12;
  }, 0);

  const toggleSub = async (sub) => {
    await api.put(`/api/subscriptions/${sub.id}`, { ...sub, active: sub.active ? 0 : 1 });
    refresh();
  };

  const delSub = async (id) => {
    await api.delete(`/api/subscriptions/${id}`);
    refresh();
  };

  const addPreset = async (preset) => {
    const existing = (subsData||[]).find(s=>s.name===preset.name);
    if (existing) { await toggleSub(existing); return; }
    await api.post('/api/subscriptions', {
      name: preset.name, amount: preset.amount, emoji: preset.emoji,
      billing_cycle: 'monthly', active: 1,
    });
    refresh();
  };

  const saveCustom = async e => {
    e.preventDefault();
    await api.post('/api/subscriptions', { ...customForm, amount:Number(customForm.amount)||0, active:1 });
    setCustomForm({ name:'', amount:'', emoji:'💳', billing_cycle:'monthly', next_renewal:'' });
    setShowCustom(false);
    refresh();
  };

  const isActive = (name) => (subsData||[]).find(s=>s.name===name&&s.active);

  return (
    <div style={{display:'flex', flexDirection:'column', gap:10}}>
      {/* Totaler */}
      <div style={b.summaryRow}>
        <div style={b.sumCard}>
          <div style={b.sumLbl}>Per måned</div>
          <div style={{...b.sumVal, color:'#7C6FF7'}}>{kr(Math.round(totalMonthly))}</div>
        </div>
        <div style={b.sumCard}>
          <div style={b.sumLbl}>Per år</div>
          <div style={{...b.sumVal, color:'#7C6FF7'}}>{kr(Math.round(totalYearly))}</div>
        </div>
        <div style={b.sumCard}>
          <div style={b.sumLbl}>Aktive</div>
          <div style={{...b.sumVal, color:'var(--txt)'}}>{activeSubs.length}</div>
        </div>
      </div>

      {/* Populære abonnementer */}
      <div style={b.card}>
        <div style={b.cardTitle}>Populære tjenester</div>
        <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
          {PRESET_SUBS.map(p => {
            const active = !!isActive(p.name);
            return (
              <button key={p.name} onClick={()=>addPreset(p)}
                style={{padding:'7px 12px', borderRadius:20, border:'none', cursor:'pointer',
                  background:active?'#7C6FF7':'var(--bg2)', color:active?'#fff':'var(--txm)', fontWeight:600,
                  fontSize:12, fontFamily:'inherit', display:'flex', alignItems:'center', gap:4}}>
                {p.emoji} {p.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Egne abonnementer */}
      {showCustom && (
        <div style={b.card}>
          <form onSubmit={saveCustom} style={b.form}>
            <div style={b.twoCol}>
              <input style={b.inp} placeholder="Tjenestenavn" value={customForm.name} onChange={setF('name')} required autoFocus />
              <input style={b.inp} placeholder="Emoji" value={customForm.emoji} onChange={setF('emoji')} maxLength={2} />
            </div>
            <div style={b.twoCol}>
              <div style={b.field}><label style={b.lbl}>Beløp (kr)</label><input style={b.inp} type="number" min="0" value={customForm.amount} onChange={setF('amount')} required /></div>
              <div style={b.field}>
                <label style={b.lbl}>Syklus</label>
                <select style={b.sel} value={customForm.billing_cycle} onChange={setF('billing_cycle')}>
                  <option value="monthly">Månedlig</option>
                  <option value="yearly">Årlig</option>
                </select>
              </div>
            </div>
            <div style={b.field}><label style={b.lbl}>Neste fornyelse</label><input style={b.inp} type="date" value={customForm.next_renewal} onChange={setF('next_renewal')} /></div>
            <div style={b.btnRow}><button style={b.saveBtn} type="submit">Legg til</button><button style={b.cancelBtn} type="button" onClick={()=>setShowCustom(false)}>Avbryt</button></div>
          </form>
        </div>
      )}

      {/* Liste over alle abonnementer */}
      {(subsData||[]).length > 0 && (
        <div style={{display:'flex', flexDirection:'column', gap:6}}>
          {(subsData||[]).map(sub => (
            <div key={sub.id} style={{...b.card, padding:'12px 16px', display:'flex', alignItems:'center', gap:12, opacity:sub.active?1:0.5}}>
              <span style={{fontSize:24, flexShrink:0}}>{sub.logo_emoji||sub.emoji||'💳'}</span>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:14, fontWeight:700, color:'var(--txt)'}}>{sub.name}</div>
                <div style={{fontSize:12, color:'var(--txl)'}}>
                  {kr(sub.amount)} / {sub.billing_cycle==='yearly'?'år':'mnd'}
                  {sub.next_renewal&&` · Fornyes ${new Date(sub.next_renewal).toLocaleDateString('nb-NO',{day:'numeric',month:'short'})}`}
                </div>
              </div>
              <button style={{...b.delBtn, ...(sub.active?{background:'#F0FDF4',color:'#065F46',border:'1px solid #86EFAC'}:{background:'#FFF5F5',color:'#C0312D',border:'1px solid #FCA5A5'})}}
                onClick={()=>toggleSub(sub)}>{sub.active?'Aktiv':'Inaktiv'}</button>
              <button style={b.delBtn} onClick={()=>delSub(sub.id)}>🗑</button>
            </div>
          ))}
        </div>
      )}

      {(subsData||[]).length === 0 && <div style={b.empty}>Ingen abonnementer lagt til ennå. Klikk på tjenestene over for å legge til.</div>}

      <button style={{...b.addBtn, marginTop:4}} onClick={()=>setShowCustom(v=>!v)}>+ Legg til eget abonnement</button>
    </div>
  );
}

// ─── BANK-FANE ─────────────────────────────────────────────────
function BankTab() {
  const [csvFile, setCsvFile] = useState(null);
  const BANKS = ['DNB','Nordea','Sparebank 1','Sbanken','Danske Bank','Handelsbanken','Vipps','Revolut'];

  return (
    <div style={{display:'flex', flexDirection:'column', gap:12}}>
      <div style={{...b.card, textAlign:'center', padding:'28px 20px'}}>
        <div style={{fontSize:48, marginBottom:10}}>🏦</div>
        <div style={{fontSize:18, fontWeight:800, color:'var(--txt)', marginBottom:6}}>Koble til nettbank</div>
        <div style={{fontSize:14, color:'var(--txm)', lineHeight:1.6, maxWidth:400, margin:'0 auto 16px'}}>
          Via <strong>Aiia (PSD2)</strong> kan du hente transaksjoner direkte fra din norske bank.
          Ingen passord lagres — kun lesetilgang via sikker bank-ID-autentisering.
        </div>
        <div style={{display:'flex', flexWrap:'wrap', gap:6, justifyContent:'center', marginBottom:20}}>
          {BANKS.map(bank=>(
            <span key={bank} style={{padding:'4px 12px', borderRadius:20, background:'var(--bg)', border:'1px solid var(--bdr)', fontSize:12, fontWeight:600, color:'var(--txm)'}}>{bank}</span>
          ))}
        </div>
        <div style={{display:'inline-flex', alignItems:'center', gap:8, background:'#FEF3C7', border:'1px solid #FDE68A', borderRadius:10, padding:'8px 16px', fontSize:13, color:'#92400E', marginBottom:16}}>
          🚧 Kommer snart — under utbygging
        </div>
        <br/>
        <button style={{...b.saveBtn, opacity:.5, cursor:'not-allowed', display:'inline-block', padding:'10px 24px'}} disabled>
          Koble til bank
        </button>
      </div>

      <div style={b.card}>
        <div style={b.cardTitle}>📤 Manuell import (CSV)</div>
        <div style={{fontSize:13, color:'var(--txm)', marginBottom:10, lineHeight:1.5}}>
          Last ned kontoutskrift som CSV fra nettbanken din og last den opp her.
        </div>
        <label style={{display:'block', padding:'20px', border:'2px dashed var(--bdr)', borderRadius:10, textAlign:'center', cursor:'pointer', color:'#7C6FF7', fontWeight:600, fontSize:14}}>
          {csvFile ? `✅ ${csvFile.name}` : '📂 Klikk for å velge CSV-fil'}
          <input type="file" accept=".csv" style={{display:'none'}} onChange={e=>setCsvFile(e.target.files?.[0]||null)} />
        </label>
        {csvFile && (
          <button style={{...b.saveBtn, marginTop:10}} onClick={()=>{ alert('CSV-import er under utvikling'); setCsvFile(null); }}>
            Importer transaksjoner
          </button>
        )}
      </div>
    </div>
  );
}

// ─── ÅRSVISNING-FANE ───────────────────────────────────────────
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Des'];

function ÅrTab({ year }) {
  const { data } = useApi(`/api/budget/yearly?year=${year}`, [year]);
  if (!data) return <div style={b.empty}>Laster årsdata…</div>;

  const expByMonth  = Object.fromEntries((data.expenses||[]).map(r => [r.month, r.total]));
  const incByMonth  = Object.fromEntries((data.income||[]).map(r => [r.month, r.total]));
  const totalExp    = Object.values(expByMonth).reduce((s,v) => s+v, 0);
  const totalInc    = Object.values(incByMonth).reduce((s,v) => s+v, 0);
  const maxVal      = Math.max(...MONTH_NAMES.map((_,i) => {
    const m = String(i+1).padStart(2,'0');
    return Math.max(expByMonth[m]||0, incByMonth[m]||0);
  }), 1);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        <div style={{...b.sumCard, textAlign:'center'}}>
          <div style={b.sumLbl}>Totale utgifter</div>
          <div style={{...b.sumVal, color:'#F7605C'}}>{kr(totalExp)}</div>
        </div>
        <div style={{...b.sumCard, textAlign:'center'}}>
          <div style={b.sumLbl}>Totale inntekter</div>
          <div style={{...b.sumVal, color:'#34D399'}}>{kr(totalInc)}</div>
        </div>
      </div>
      <div style={b.card}>
        <div style={{fontSize:13, fontWeight:700, color:'var(--txl)', marginBottom:12}}>Månedlig oversikt {year}</div>
        {MONTH_NAMES.map((name, i) => {
          const m   = String(i+1).padStart(2,'0');
          const exp = expByMonth[m] || 0;
          const inc = incByMonth[m] || 0;
          const expPct = Math.round((exp / maxVal) * 100);
          const incPct = Math.round((inc / maxVal) * 100);
          return (
            <div key={m} style={{ marginBottom:10 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, fontWeight:600, color:'var(--txm)', marginBottom:4 }}>
                <span>{name}</span>
                <span style={{ color:'#F7605C' }}>{kr(exp)}</span>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                <div style={{ background:'var(--bdr)', borderRadius:4, height:8, overflow:'hidden' }}>
                  <div style={{ width:`${expPct}%`, height:'100%', background:'#F7605C', borderRadius:4, transition:'.4s' }}/>
                </div>
                {inc > 0 && (
                  <div style={{ background:'var(--bdr)', borderRadius:4, height:8, overflow:'hidden' }}>
                    <div style={{ width:`${incPct}%`, height:'100%', background:'#34D399', borderRadius:4, transition:'.4s' }}/>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div style={{ display:'flex', gap:16, marginTop:8, fontSize:11, color:'var(--txl)' }}>
          <span><span style={{display:'inline-block',width:10,height:10,background:'#F7605C',borderRadius:2,marginRight:4}}/>Utgifter</span>
          <span><span style={{display:'inline-block',width:10,height:10,background:'#34D399',borderRadius:2,marginRight:4}}/>Inntekter</span>
        </div>
      </div>
    </div>
  );
}

// ─── HOVED-KOMPONENT ───────────────────────────────────────────
export default function BudgetPage() {
  const today = new Date();
  const { members } = useAuth();
  const toast = useToast();
  const { dark } = useTheme();
  const isMobile = useMobile();
  const [tab, setTab]   = useState('oversikt');
  const [month, setMonth] = useState(today.toISOString().slice(0,7));
  const [yearView, setYearView] = useState(today.getFullYear());

  const { data, refresh } = useApi(`/api/budget?month=${month}`, [month]);
  const { data: incomeData, refresh: refreshIncome } = useApi(`/api/income?month=${month}`, [month]);
  const { data: subsData,   refresh: refreshSubs   } = useApi('/api/subscriptions');

  const categories = data?.categories || [];
  const expenses   = data?.expenses   || [];
  const income     = incomeData || [];
  const subs       = Array.isArray(subsData) ? subsData : [];

  const catStats = useMemo(() => categories.map(c => {
    const catExp = expenses.filter(e => e.category_id === c.id);
    return { ...c, spent: catExp.reduce((s,e) => s+e.amount, 0), expenses: catExp };
  }), [categories, expenses]);

  const totalBudget = catStats.reduce((s,c) => s+(c.budget||0), 0);
  const totalSpent  = catStats.reduce((s,c) => s+c.spent, 0);

  const prevMonth = () => { const d=new Date(month+'-01'); d.setMonth(d.getMonth()-1); setMonth(d.toISOString().slice(0,7)); };
  const nextMonth = () => { const d=new Date(month+'-01'); d.setMonth(d.getMonth()+1); setMonth(d.toISOString().slice(0,7)); };
  const monthLabel = new Date(month+'-15').toLocaleDateString('nb-NO',{month:'long',year:'numeric'});

  const doRefresh = () => { refresh(); refreshIncome(); };

  const exportCSV = () => {
    const token = localStorage.getItem('token');
    const isYear = tab === 'år';
    const url = isYear
      ? `/api/budget/export?year=${yearView}&token=${token}`
      : `/api/budget/export?month=${month}&token=${token}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = isYear ? `budsjett-${yearView}.csv` : `budsjett-${month}.csv`;
    a.click();
    toast.success('CSV-fil lastes ned');
  };

  const TABS = [
    { id:'oversikt',      label:'Oversikt'      },
    { id:'inntekter',     label:'Inntekter'     },
    { id:'utgifter',      label:'Utgifter'      },
    { id:'abonnementer',  label:'Abonnementer'  },
    { id:'år',            label:'Årsvisning'    },
    { id:'bank',          label:'Bank'          },
  ];

  const pageBg = dark ? '#0A0818' : '#EDE8FF';

  return (
    <div style={{...b.wrap, ...(isMobile ? { background:pageBg, minHeight:'100vh' } : {})}}>
      {/* Gradient Hero Header (mobile) / Month nav (desktop) */}
      {isMobile ? (
        <div style={{
          background: dark
            ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
            : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
          padding: '52px 20px 20px',
          position: 'relative',
          overflow: 'hidden',
          margin: '-12px -12px 0',
        }}>
          <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
          <div style={{ position:'relative', zIndex:1, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500, marginBottom:2 }}>Famora</div>
              <div style={{ fontSize:24, fontWeight:800, color:'#fff' }}>Budsjett</div>
              <div style={{ fontSize:14, color:'rgba(255,255,255,0.7)', marginTop:4 }}>{tab !== 'år' ? monthLabel : yearView}</div>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {tab !== 'år' ? (
                <>
                  <button onClick={prevMonth} style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:10, width:36, height:36, color:'#fff', fontSize:20, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>‹</button>
                  <button onClick={nextMonth} style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:10, width:36, height:36, color:'#fff', fontSize:20, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>›</button>
                </>
              ) : (
                <>
                  <button onClick={() => setYearView(y => y-1)} style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:10, width:36, height:36, color:'#fff', fontSize:20, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>‹</button>
                  <button onClick={() => setYearView(y => y+1)} style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:10, width:36, height:36, color:'#fff', fontSize:20, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>›</button>
                </>
              )}
              <button onClick={exportCSV} title="Last ned CSV" style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:10, padding:'8px 10px', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:700 }}>⬇️</button>
            </div>
          </div>
        </div>
      ) : (
        tab !== 'år' ? (
          <div style={b.monthNav}>
            <button style={b.navBtn} onClick={prevMonth}>‹</button>
            <span style={b.monthLabel}>{monthLabel}</span>
            <button style={b.navBtn} onClick={nextMonth}>›</button>
          </div>
        ) : (
          <div style={b.monthNav}>
            <button style={b.navBtn} onClick={() => setYearView(y => y-1)}>‹</button>
            <span style={b.monthLabel}>{yearView}</span>
            <button style={b.navBtn} onClick={() => setYearView(y => y+1)}>›</button>
          </div>
        )
      )}

      {/* Faner + eksport */}
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <div style={{...b.tabs, flex:1}}>
          {TABS.map(t => (
            <button key={t.id} style={{...b.tab,...(tab===t.id?b.tabActive:{})}} onClick={()=>setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
        {!isMobile && <button onClick={exportCSV} title="Last ned CSV" style={{ padding:'8px 10px', borderRadius:9, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txm)', cursor:'pointer', fontSize:13, fontFamily:'inherit', flexShrink:0, fontWeight:700 }}>
          ⬇️ CSV
        </button>}
      </div>

      {/* Innhold */}
      {tab === 'oversikt'     && <OversiktTab catStats={catStats} totalBudget={totalBudget} totalSpent={totalSpent} expenses={expenses} incomeData={income} month={month} />}
      {tab === 'inntekter'    && <InntekterTab incomeData={income} refresh={refreshIncome} month={month} members={members} />}
      {tab === 'utgifter'     && <UtgifterTab catStats={catStats} expenses={expenses} refresh={doRefresh} categories={categories} members={members} />}
      {tab === 'abonnementer' && <AbonnementerTab subsData={subs} refresh={refreshSubs} />}
      {tab === 'år'           && <ÅrTab year={yearView} />}
      {tab === 'bank'         && <BankTab />}
    </div>
  );
}

// ─── Stiler ────────────────────────────────────────────────────
const b = {
  wrap:        { display:'flex', flexDirection:'column', gap:12 },
  monthNav:    { display:'flex', alignItems:'center', justifyContent:'space-between', background:'var(--card)', borderRadius:14, padding:'12px 16px', border:'1px solid var(--bdr)' },
  navBtn:      { padding:'7px 14px', borderRadius:9, border:'1px solid var(--bdr)', background:'var(--bg)', color:'#7C6FF7', fontWeight:700, cursor:'pointer', fontSize:14, fontFamily:'inherit' },
  monthLabel:  { fontSize:15, fontWeight:800, color:'var(--txt)' },
  tabs:        { display:'flex', gap:2, background:'var(--bg)', borderRadius:12, padding:4, border:'1px solid var(--bdr)', overflowX:'auto' },
  tab:         { flex:1, padding:'8px 6px', borderRadius:9, border:'none', background:'transparent', color:'var(--txm)', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit', whiteSpace:'nowrap' },
  tabActive:   { background:'#7C6FF7', color:'#fff' },
  summaryRow:  { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 },
  sumCard:     { background:'var(--card)', borderRadius:14, padding:'14px 16px', border:'1px solid var(--bdr)', textAlign:'center' },
  sumLbl:      { fontSize:11, fontWeight:700, color:'var(--txl)', textTransform:'uppercase', letterSpacing:.4, marginBottom:4 },
  sumVal:      { fontSize:22, fontWeight:800, color:'var(--txt)' },
  card:        { background:'var(--card)', borderRadius:14, padding:'14px 16px', border:'1px solid var(--bdr)' },
  cardTitle:   { fontSize:13, fontWeight:800, color:'var(--txt)', marginBottom:10 },
  form:        { display:'flex', flexDirection:'column', gap:10 },
  twoCol:      { display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 },
  field:       { display:'flex', flexDirection:'column', gap:4 },
  lbl:         { fontSize:11, fontWeight:700, color:'var(--txm)', textTransform:'uppercase', letterSpacing:.4 },
  inp:         { padding:'9px 12px', borderRadius:9, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:13, color:'var(--input-txt)', outline:'none', background:'var(--input-bg)' },
  sel:         { padding:'9px 12px', borderRadius:9, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:13, color:'var(--input-txt)', outline:'none', background:'var(--input-bg)' },
  btnRow:      { display:'flex', gap:8 },
  saveBtn:     { flex:1, padding:'10px', borderRadius:10, border:'none', background:'#7C6FF7', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:14, fontFamily:'inherit' },
  cancelBtn:   { flex:1, padding:'10px', borderRadius:10, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txm)', fontWeight:600, cursor:'pointer', fontSize:14, fontFamily:'inherit' },
  addBtn:      { padding:'11px', borderRadius:12, border:'2px dashed #7C6FF7', background:'var(--bg)', color:'#7C6FF7', fontWeight:700, cursor:'pointer', fontSize:14, fontFamily:'inherit', textAlign:'center' },
  empty:       { background:'var(--card)', borderRadius:14, padding:'24px', border:'1px solid var(--bdr)', textAlign:'center', color:'var(--txl)', fontSize:13 },
  catCard:     { background:'var(--card)', borderRadius:14, border:'1px solid var(--bdr)', overflow:'hidden' },
  catTop:      { display:'flex', alignItems:'center', gap:12, padding:'14px 16px', cursor:'pointer', userSelect:'none' },
  catIcon:     { width:42, height:42, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 },
  catName:     { fontSize:14, fontWeight:700, color:'var(--txt)' },
  catRight:    { display:'flex', flexDirection:'column', alignItems:'flex-end', gap:2, flexShrink:0 },
  catSpent:    { fontSize:16, fontWeight:800 },
  catBudget:   { fontSize:11, color:'var(--txl)' },
  expRow:      { display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderBottom:'1px solid var(--bdr)' },
  expTitle:    { fontSize:14, fontWeight:600, color:'var(--txt)' },
  expMeta:     { fontSize:12, color:'var(--txl)', marginTop:2 },
  expAmount:   { fontSize:14, fontWeight:700, color:'var(--txt)', flexShrink:0 },
  delBtn:      { background:'none', border:'none', color:'var(--txl)', fontSize:14, cursor:'pointer', padding:'2px 4px', fontFamily:'inherit', flexShrink:0, lineHeight:1 },
};
